import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { chatApi } from 'api/chat'
import {
  IChat,
  IChatListUpdatedPayload,
  IChatMessage,
  IChatMessageCreatedPayload
} from 'models/chat'
import { showMessage } from 'shared/utils/showMessage'
import { getMessagePreview } from 'components/chats/utils'
import { ChatState } from './types'

const initialState: ChatState = {
  chats: [],
  activeChat: null,
  messages: [],
  unreadCount: 0,
  isChatsLoading: false,
  isChatLoading: false,
  isMessagesLoading: false,
  error: null
}

const upsertChat = (chats: IChat[], chat: IChat) => {
  const existingIndex = chats.findIndex((item) => item._id === chat._id)
  const nextChat = existingIndex >= 0 ? { ...chats[existingIndex], ...chat } : chat
  const nextChats = existingIndex >= 0
    ? chats.filter((item) => item._id !== chat._id)
    : chats

  return [nextChat, ...nextChats].sort((a, b) => {
    const dateA = new Date(a.last_message_at || 0).getTime()
    const dateB = new Date(b.last_message_at || 0).getTime()
    return dateB - dateA
  })
}

interface IGetChatsParams {
  sellerId?: string
  adminMode?: boolean
}

interface IChatRequestParams {
  chatId: string
  adminMode?: boolean
}

export const getChats = createAsyncThunk<void, IGetChatsParams | undefined>('chat/getChats', async (params, { dispatch }) => {
  try {
    const response = params?.adminMode && params.sellerId
      ? await chatApi.getAdminSellerChats(params.sellerId)
      : await chatApi.getChats()
    dispatch(chatActions.setChats(response.data))
  } catch (e) {
    showMessage('error', 'Ошибка', 'Не удалось загрузить чаты')
    throw e
  }
})

export const getChat = createAsyncThunk<void, string | IChatRequestParams>(
  'chat/getChat',
  async (params, { dispatch }) => {
    try {
      const chatId = typeof params === 'string' ? params : params.chatId
      const response = typeof params === 'string' || !params.adminMode
        ? await chatApi.getChat(chatId)
        : await chatApi.getAdminChat(chatId)
      dispatch(chatActions.setActiveChat(response.data))
    } catch (e) {
      showMessage('error', 'Ошибка', 'Не удалось загрузить чат')
      throw e
    }
  }
)

export const getChatMessages = createAsyncThunk<void, string | IChatRequestParams>(
  'chat/getChatMessages',
  async (params, { dispatch }) => {
    try {
      const chatId = typeof params === 'string' ? params : params.chatId
      const response = typeof params === 'string' || !params.adminMode
        ? await chatApi.getMessages(chatId)
        : await chatApi.getAdminMessages(chatId)
      dispatch(chatActions.setMessages(response.data))
    } catch (e) {
      showMessage('error', 'Ошибка', 'Не удалось загрузить сообщения')
      throw e
    }
  }
)

export const getChatUnreadCount = createAsyncThunk(
  'chat/getChatUnreadCount',
  async (_, { dispatch }) => {
    try {
      const response = await chatApi.getUnreadCount()
      dispatch(chatActions.setUnreadCount(response.data.unreadCount))
    } catch (e) {
      showMessage('error', 'Ошибка', 'Не удалось загрузить счетчик сообщений')
      throw e
    }
  }
)

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChats(state, action: PayloadAction<IChat[]>) {
      state.chats = action.payload
    },
    appendChats(state, action: PayloadAction<IChat[]>) {
      action.payload.forEach((chat) => {
        state.chats = upsertChat(state.chats, chat)
      })
    },
    setActiveChat(state, action: PayloadAction<IChat | null>) {
      state.activeChat = action.payload
      if (action.payload) {
        state.chats = upsertChat(state.chats, action.payload)
      }
    },
    setMessages(state, action: PayloadAction<IChatMessage[]>) {
      state.messages = action.payload
      if (state.activeChat && !state.activeChat.last_product) {
        const lastProduct = [...action.payload].reverse().find((item) => item.product_id)?.product_id
        if (lastProduct) {
          state.activeChat.last_product = lastProduct
        }
      }
    },
    addMessage(state, action: PayloadAction<IChatMessage>) {
      const messageExists = state.messages.some((item) => item._id === action.payload._id)
      if (!messageExists) {
        state.messages.push(action.payload)
      }
      if (state.activeChat && action.payload.product_id) {
        state.activeChat.last_product = action.payload.product_id
      }
    },
    applyMessageCreated(state, action: PayloadAction<IChatMessageCreatedPayload>) {
      const { chatId, chat, message } = action.payload
      const existingChat = state.chats.find((item) => item._id === chatId)
      const updatedChat = chat || (existingChat
        ? {
            ...existingChat,
            last_product: message.product_id || existingChat.last_product,
            last_message_text: getMessagePreview(message),
            last_message_at: message.createdAt || existingChat.last_message_at,
            last_message_sender_type: message.sender_type
          }
        : null)

      if (state.activeChat?._id === chatId) {
        const messageExists = state.messages.some((item) => item._id === message._id)
        if (!messageExists) {
          state.messages.push(message)
        }
        state.activeChat = {
          ...state.activeChat,
          ...(updatedChat || {}),
          last_product: updatedChat?.last_product || message.product_id || state.activeChat.last_product,
          last_message_text: getMessagePreview(message),
          last_message_at: message.createdAt || state.activeChat.last_message_at,
          last_message_sender_type: message.sender_type
        }
      }
      if (updatedChat) {
        state.chats = upsertChat(state.chats, updatedChat)
      }
    },
    applyMessageUpdated(state, action: PayloadAction<IChatMessageCreatedPayload>) {
      const { chatId, message } = action.payload
      if (state.activeChat?._id !== chatId) return

      state.messages = state.messages.map((item) => (item._id === message._id ? message : item))
    },
    applyChatListUpdated(state, action: PayloadAction<IChatListUpdatedPayload>) {
      state.chats = upsertChat(state.chats, action.payload.chat)
      if (state.activeChat?._id === action.payload.chat._id) {
        state.activeChat = {
          ...state.activeChat,
          ...action.payload.chat
        }
      }
    },
    setUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = action.payload
    },
    clearChatState() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChats.pending, (state) => {
        state.isChatsLoading = true
        state.error = null
      })
      .addCase(getChats.fulfilled, (state) => {
        state.isChatsLoading = false
      })
      .addCase(getChats.rejected, (state) => {
        state.isChatsLoading = false
        state.error = 'Не удалось загрузить чаты'
      })
      .addCase(getChat.pending, (state) => {
        state.isChatLoading = true
      })
      .addCase(getChat.fulfilled, (state) => {
        state.isChatLoading = false
      })
      .addCase(getChat.rejected, (state) => {
        state.isChatLoading = false
      })
      .addCase(getChatMessages.pending, (state) => {
        state.isMessagesLoading = true
      })
      .addCase(getChatMessages.fulfilled, (state) => {
        state.isMessagesLoading = false
      })
      .addCase(getChatMessages.rejected, (state) => {
        state.isMessagesLoading = false
      })
      .addCase('app/logout', () => initialState)
  }
})

export const chatActions = chatSlice.actions
export default chatSlice.reducer
